import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, CheckCircle, XCircle, Clock } from 'lucide-react';
import { API, authFetch } from '@/App';
import { toast } from 'sonner';
import GlowBadge from '@/components/GlowBadge';

export default function BadgeRequestPage() {
  const navigate = useNavigate();
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [badgeData, setBadgeData] = useState(null);

  useEffect(() => { fetchStatus(); }, []);

  const fetchStatus = async () => {
    try {
      const response = await authFetch(`${API}/badge/my-request`);
      if (response.ok) setBadgeData(await response.json());
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (reason.trim().length < 10) {
      toast.error('Please write at least 10 characters');
      return;
    }
    setSubmitting(true);
    try {
      const response = await authFetch(`${API}/badge/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason })
      });
      if (response.ok) {
        toast.success('Badge request submitted!');
        fetchStatus();
        setReason('');
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to submit');
      }
    } catch (error) {
      toast.error('Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const currentBadge = badgeData?.current_badge;
  const request = badgeData?.request;

  return (
    <div className="min-h-screen pb-24">
      <div className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          <button data-testid="back-btn" onClick={() => navigate(-1)} className="p-2 hover:bg-primary/10 rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Glow Badge</h1>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Badge Info */}
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20 rounded-2xl p-6 border border-amber-200/50 dark:border-amber-800/30">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex gap-2">
              <GlowBadge tier="glow" size="lg" />
              <GlowBadge tier="glow_pro" size="lg" />
            </div>
            <h2 className="text-lg font-bold">What is the Glow Badge?</h2>
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed">
            The Glow Badge recognizes influential beauty creators on Gloverse. 
            <strong> Glow</strong> is for verified creators, and <strong> Glow Pro</strong> is for top-tier influencers with exceptional engagement and content.
          </p>
          <div className="mt-3 bg-white/60 rounded-xl px-4 py-2.5 border border-amber-200/50">
            <p className="text-xs text-amber-800 font-medium">
              Requires at least <strong>5,000 followers</strong> to apply. Exceptions made for select creators by invitation.
            </p>
          </div>
        </div>

        {/* Current Badge Status */}
        {currentBadge && (
          <div className="bg-green-50 dark:bg-green-950/20 rounded-2xl p-6 border border-green-200/50 dark:border-green-800/30 flex items-center gap-4">
            <CheckCircle className="w-8 h-8 text-green-600 shrink-0" />
            <div>
              <p className="font-bold text-green-800 dark:text-green-200">
                You have the {currentBadge === 'glow_pro' ? 'Glow Pro' : 'Glow'} Badge!
              </p>
              <div className="flex items-center gap-1.5 mt-1">
                <span className="text-sm text-muted-foreground">Your badge:</span>
                <GlowBadge tier={currentBadge} />
              </div>
            </div>
          </div>
        )}

        {/* Pending Request */}
        {!currentBadge && request?.status === 'pending' && (
          <div className="bg-amber-50 dark:bg-amber-950/20 rounded-2xl p-6 border border-amber-200/50 dark:border-amber-800/30 flex items-center gap-4">
            <Clock className="w-8 h-8 text-amber-600 shrink-0" />
            <div>
              <p className="font-bold text-amber-800 dark:text-amber-200">Request Pending</p>
              <p className="text-sm text-muted-foreground mt-1">Your badge request is being reviewed. We'll notify you when there's an update.</p>
            </div>
          </div>
        )}

        {/* Rejected Request */}
        {!currentBadge && request?.status === 'rejected' && (
          <div className="bg-red-50 dark:bg-red-950/20 rounded-2xl p-6 border border-red-200/50 dark:border-red-800/30 flex items-center gap-4">
            <XCircle className="w-8 h-8 text-red-600 shrink-0" />
            <div>
              <p className="font-bold text-red-800 dark:text-red-200">Request Not Approved</p>
              <p className="text-sm text-muted-foreground mt-1">
                Your previous request was not approved. You can submit a new request below.
              </p>
            </div>
          </div>
        )}

        {/* Request Form */}
        {!currentBadge && request?.status !== 'pending' && (
          <div className="bg-card rounded-2xl p-6 border border-border space-y-4">
            <h3 className="font-bold text-lg">Apply for the Glow Badge</h3>
            {(badgeData?.followers_count || 0) < 5000 ? (
              <div className="bg-muted rounded-xl p-4 text-center space-y-2">
                <p className="text-sm text-muted-foreground">
                  You need <strong className="text-foreground">5,000 followers</strong> to apply.
                </p>
                <p className="text-xs text-muted-foreground">
                  You currently have <strong className="text-foreground">{badgeData?.followers_count || 0}</strong> followers. Keep creating great content!
                </p>
              </div>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">
                  Tell us why you should receive the Glow Badge. Mention your niche, content style, and what makes you stand out.
                </p>
                <textarea
                  data-testid="badge-reason-input"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="I create content about skincare routines and have been actively sharing product reviews..."
                  className="w-full p-4 rounded-xl bg-muted border border-border focus:border-primary focus:ring-1 focus:ring-primary transition-colors resize-none text-sm"
                  rows={5}
                  maxLength={500}
                />
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">{reason.length}/500</span>
                  <button
                    data-testid="submit-badge-request"
                    onClick={handleSubmit}
                    disabled={submitting || reason.trim().length < 10}
                    className="btn-primary px-6 py-2.5 flex items-center gap-2 disabled:opacity-50"
                  >
                    <Send className="w-4 h-4" />
                    {submitting ? 'Submitting...' : 'Submit Request'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Stats */}
        <div className="bg-card rounded-2xl p-6 border border-border">
          <h3 className="font-bold mb-3">Your Stats</h3>
          <div className="text-sm text-muted-foreground">
            <p>Followers: <strong className="text-foreground">{badgeData?.followers_count || 0}</strong></p>
          </div>
        </div>
      </div>
    </div>
  );
}
